"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getGenerateFn = exports.generate = exports.evaluateRuleExpressions = void 0;
const parsers_1 = __importDefault(require("../parsers"));
const functions_1 = __importDefault(require("../functions"));
const value_wrappers_1 = require("../value-wrappers");
const NO_CODE_KEY = '--No Code Key--';
const hasNoCodeKey = (codeKey) => codeKey === NO_CODE_KEY;
const ruleEvaluators = {
    boolean: parsers_1.default.boolean,
    expressionList: parsers_1.default.evaluateExpressionList,
    generator: (v) => v,
    integerExpression: parsers_1.default.evaluateExpression,
    latexWithPlaceholders: parsers_1.default.evaluateLatexPlaceholders,
    nameValueList: parsers_1.default.evaluateNameValueList,
    operator: parsers_1.default.operator,
    questionSteps: parsers_1.default.questionSteps,
    select: (v) => v,
    table: parsers_1.default.evaluateTable,
    textWithPlaceholders: parsers_1.default.evaluatePlaceholders,
    textWithPlaceholdersArray: parsers_1.default.evaluateTextWithPlaceholdersArray,
    textWithPlaceholdersTable: parsers_1.default.evaluateTextWithPlaceholdersTable,
    tokenArray: parsers_1.default.tokeniseString,
    valueNamesList: parsers_1.default.evaluateValueNamesList,
};
function getEvaluator(ruleKey, ruleTypes) {
    const ruleType = ruleTypes[ruleKey];
    if (!ruleType)
        return () => (0, value_wrappers_1.makeError)(`No evaluator is set for the "${ruleKey}" rule in the function spec.`);
    const evaluator = ruleEvaluators[ruleType];
    if (!evaluator)
        return () => (0, value_wrappers_1.makeError)(`There is no evaluator for rules of type "${ruleType}" (in the "${ruleKey}" rule).`);
    return evaluator;
}
function addTokenTypes(token, valuesArray, typeSpecs = {}) {
    var _a, _b, _c, _d, _e;
    if (!token.name)
        return token;
    const [firstTokenName, ...rest] = token.name.split('.');
    let type = (_b = ((_a = valuesArray.find((v) => v.name === firstTokenName)) !== null && _a !== void 0 ? _a : {}).type) !== null && _b !== void 0 ? _b : token.type;
    for (const valueNameField of rest) {
        const typeDetails = typeof type === 'string'
            ? typeSpecs[type]
            : type.fields.reduce((soFar, field) => {
                soFar.fields[field.name] = field.type;
                return soFar;
            }, { fields: {} });
        const typeDetailsFields = (_c = typeDetails === null || typeDetails === void 0 ? void 0 : typeDetails.inputs) !== null && _c !== void 0 ? _c : typeDetails === null || typeDetails === void 0 ? void 0 : typeDetails.fields;
        type = (_e = (_d = typeDetailsFields === null || typeDetailsFields === void 0 ? void 0 : typeDetailsFields[valueNameField]) !== null && _d !== void 0 ? _d : typeDetails === null || typeDetails === void 0 ? void 0 : typeDetails.functionId) !== null && _e !== void 0 ? _e : 'none';
    }
    return Object.assign(Object.assign({}, token), { type });
}
const addTokenTypesForRuleTypes = {
    tokenArray: (wrappedValue, valuesArray, typeSpecs) => wrappedValue.value.map((token) => {
        return addTokenTypes(token, valuesArray, typeSpecs);
    }),
    questionSteps: (wrappedValue, valuesArray, typeSpecs) => {
        var _a;
        if ((_a = wrappedValue.value[0]) === null || _a === void 0 ? void 0 : _a.items) {
            return wrappedValue.value.map((step) => {
                return Object.assign(Object.assign({}, step), { items: step.items.map((token) => {
                        if (Array.isArray(token.value)) {
                            token.value = token.value.map((tokens) => addTokenTypes(tokens, valuesArray, typeSpecs));
                        }
                        return token;
                    }) });
            });
        }
        return wrappedValue.value.map((token) => {
            if (token.type === 'string' && Array.isArray(token.value)) {
                token.value = token.value.map((tokens) => addTokenTypes(tokens, valuesArray, typeSpecs));
            }
            return token;
        });
    },
};
function evaluateRuleExpressions(rules, state, ruleTypes, valuesArray, typeSpecs = {}) {
    var _a, _b;
    let error;
    const evaluatedRules = [];
    const variables = new Set();
    const ruleEntries = Object.entries(rules !== null && rules !== void 0 ? rules : {});
    for (let i = 0; i < ruleEntries.length && !error; i++) {
        const [key, expression] = ruleEntries[i];
        let wrappedValue = getEvaluator(key, ruleTypes)(expression, state);
        for (const v of (_a = wrappedValue.variables) !== null && _a !== void 0 ? _a : [])
            variables.add(v);
        if (wrappedValue.kind !== 'error') {
            const ruleType = ruleTypes[key];
            if (['tokenArray', 'questionSteps'].includes(ruleType)) {
                const addExtraTokenTypes = addTokenTypesForRuleTypes[ruleTypes[key]];
                wrappedValue = (_b = addExtraTokenTypes(wrappedValue, valuesArray, typeSpecs)) !== null && _b !== void 0 ? _b : wrappedValue;
            }
            evaluatedRules.push([key, wrappedValue]);
        }
        else {
            error = wrappedValue;
        }
    }
    return {
        error,
        evaluatedRules: error ? {} : Object.fromEntries(evaluatedRules),
        variables: [...variables],
    };
}
exports.evaluateRuleExpressions = evaluateRuleExpressions;
function generate(functionSpec, rules, state, valuesArray, typeSpecs = {}) {
    var _a;
    const { codeKey, functionCodeKey, ruleTypes } = functionSpec !== null && functionSpec !== void 0 ? functionSpec : {};
    // const mustPassToFunction = !hasNoCodeKey(codeKey);
    // Get the actual js generator function from the lookup object imported as 'fns'.
    // @ts-ignore
    const generatorFn = (_a = functions_1.default[codeKey]) !== null && _a !== void 0 ? _a : functions_1.default[functionCodeKey];
    // if (!generatorFn && mustPassToFunction) return makeError(
    //   `The code key "${codeKey}" for the function spec "${name}" does not match a js generator function.`
    // );
    // The rules may include expressions that need to be evaluated before the js generator function is called.
    // The expressions usually include variables that are part of the state. E.g. 'n1 + 5' or ${name}.
    // How to evaluate the expressions depends on the type of rule.
    const { error, evaluatedRules, variables } = evaluateRuleExpressions(rules, state, ruleTypes, valuesArray, typeSpecs);
    if (error)
        return error;
    if (!Object.keys(evaluatedRules).length && (generatorFn === null || generatorFn === void 0 ? void 0 : generatorFn.length)) {
        return (0, value_wrappers_1.makeError)('No rules were supplied to the value function');
    }
    const value = generatorFn ? generatorFn((0, value_wrappers_1.unwrapState)(evaluatedRules)) : Object.values((0, value_wrappers_1.unwrapState)(evaluatedRules))[0];
    return (0, value_wrappers_1.makeValue)(value, variables);
}
exports.generate = generate;
// Pass in a generator function specification to get a generator function for that spec.
// The return value also specifies the previous values that the generator depends on.
function getGenerateFn(functionSpec, valuesArray, typeSpecs = {}) {
    return (rules, state) => generate(functionSpec, rules, (0, value_wrappers_1.unwrapState)(state), valuesArray, typeSpecs);
}
exports.getGenerateFn = getGenerateFn;
